import {
	getTreeCell,
	GridEvents,
	IAdjustBy,
	IEventHandlersMap,
	isSortable,
	ProGrid,
	getTotalWidth,
	ICellRect,
	getTotalHeight,
	getMaxColsWidth,
	IRow,
	ICol,
	IColumnsWidth,
	getHeight,
	getWidth,
	ISpan,
} from "@dhx/ts-grid";
import { findIndex, isDefined } from "@dhx/ts-common/core";
import { IEventSystem } from "@dhx/ts-common/events";
import {
	DataEvents,
	DragEvents,
	IDataEventsHandlersMap,
	IDragEventsHandlersMap,
	IDataItem,
	DataDriver,
	toDataDriver,
} from "@dhx/ts-data";
import { TreeGridCollection } from "./TreeGridCollection";
import { ITreeEventHandlersMap, ITreeGrid, ITreeGridConfig, TreeGridEvents } from "./types";
import { Id } from "@dhx/ts-common/types";

const defaultGroupTemplate = (groupName: string, group: IDataItem[]) =>
	`${groupName || ""} (${group.length})`;
const groupRender = (row, config) =>
	getTreeCell(
		config.groupTitleTemplate(row.$groupName, row.items),
		row,
		{ id: "$groupName", $width: "100%" as any, $cellCss: {} },
		config
	);

export class TreeGrid extends ProGrid implements ITreeGrid {
	data: TreeGridCollection;
	events: IEventSystem<
		DataEvents | GridEvents | DragEvents | TreeGridEvents,
		IEventHandlersMap & IDataEventsHandlersMap & IDragEventsHandlersMap & ITreeEventHandlersMap
	>;
	private _pregroupData: any[];

	constructor(container: HTMLElement, config: ITreeGridConfig) {
		// [dirty]
		config.keyNavigation = false;
		config.multiselection = false;

		super(container, config);
		// [dirty]
		if (!this.config.dropBehaviour) {
			this.config.dropBehaviour = "complex";
		}
		this.config.type = "tree";
	}
	scrollTo(rowId: Id, colId: Id) {
		const selectedCell = this.selection.getCell();

		const columns = this.config.columns.filter(col => !col.hidden);
		const colInd = findIndex(columns, ({ id }) => id == colId);
		const prevCol = selectedCell ? selectedCell.column : this.config.columns[0];
		const prevColInd = findIndex(columns, obj => obj.id == prevCol.id);
		const fixedColsWidth = this.config.leftSplit
			? getTotalWidth(columns.slice(0, this.config.leftSplit))
			: 0;
		const x = getTotalWidth(columns.slice(0, colInd)) - (colInd - prevColInd < 0 ? fixedColsWidth : 0);

		const rows = this.data.mapVisible(i => i);
		const rowInd = findIndex(rows, ({ id }) => id == rowId);
		const y = getTotalHeight(rows.slice(0, rowInd));

		const scrollState = this.getScrollState();

		const gridRight = this.config.width + scrollState.x;
		const gridBottom =
			this.config.height + scrollState.y - this.config.headerRowHeight * this.config.$headerLevel;

		const cellTop = y - scrollState.y - rows[rowInd].$height;
		const cellLeft = x - scrollState.x - columns[colInd].$width;

		const cellBottom = y + rows[rowInd].$height * 2 + 18 - gridBottom;
		const cellRight = x + columns[colInd].$width * 2 + 18 - gridRight;

		const scrollTop = cellTop > 0 && cellBottom < 0 ? 0 : cellTop < 0 ? cellTop : cellBottom;
		const scrollLeft = cellLeft > 0 && cellRight < 0 ? 0 : cellLeft < 0 ? cellLeft : cellRight;

		this.scroll(scrollLeft + scrollState.x, scrollTop + scrollState.y);
	}
	expand(rowId: Id): void {
		if (this.data.haveItems(rowId)) {
			if (!this.events.fire(TreeGridEvents.beforeExpand, [rowId])) {
				return;
			}
			this.data.update(rowId, { $opened: true });
			this.events.fire(TreeGridEvents.afterExpand, [rowId]);
		}
	}
	collapse(rowId: Id): void {
		if (this.data.haveItems(rowId)) {
			if (!this.events.fire(TreeGridEvents.beforeCollapse, [rowId])) {
				return;
			}
			this.data.update(rowId, { $opened: false });
			this.events.fire(TreeGridEvents.afterCollapse, [rowId]);
		}
	}
	expandAll(): void {
		this.data.eachChild(this.data.getRoot(), ({ id }) => this.expand(id));
	}
	collapseAll(): void {
		this.data.forEach(item => {
			if (this.data.haveItems(item.id)) {
				this.collapse(item.id);
			}
		});
	}
	groupBy(property: string | ((item: IDataItem) => string)): void {
		this.ungroup();
		this.config.groupTitleTemplate = this.config.groupTitleTemplate || defaultGroupTemplate;
		this._pregroupData = this._serialize();
		const grouppedData = this._groupBy(this._serialize(), property);
		this.data.parse(grouppedData);

		grouppedData.forEach(i => {
			this.addRowCss(i.id, "dhx_tree-cell_group-title");
		});
	}
	ungroup(): void {
		if (this._pregroupData) {
			this.data.parse(this._pregroupData);
			this._pregroupData = null;
		}
	}
	showRow(rowId: Id): void {
		if (!isDefined(rowId)) {
			return;
		}

		const row = this.data.getItem(rowId);
		if (!row || !row.hidden) {
			return;
		}

		if (!this.events.fire(GridEvents.beforeRowShow, [row])) return;
		this.data.update(rowId, { hidden: false, wasHidden: false });
		this.data.restoreOrder();
		this.data.eachChild(rowId, i => {
			if (!i.wasHidden) {
				this.data.update(i.id, { hidden: false });
			}
		});
		this.data.filter(i => !i.hidden);
		this.events.fire(GridEvents.afterRowShow, [row]);
	}
	hideRow(rowId: Id): void {
		if (!isDefined(rowId)) {
			return;
		}

		const row = this.data.getItem(rowId);
		if (!row || row.hidden) {
			return;
		}

		if (!this.events.fire(GridEvents.beforeRowShow, [row])) return;
		this.data.update(rowId, { hidden: true, wasHidden: true });

		this.data.eachChild(rowId, i => this.data.update(i.id, { hidden: true }));
		this.data.filter(i => !i.hidden);
		this.events.fire(GridEvents.afterRowShow, [row]);
	}
	getCellRect(rowId: Id, colId: Id): ICellRect {
		const columns = this.config.columns.filter(col => !col.hidden);
		const rows = this.data.mapVisible(i => i);
		const span = this.getSpan(rowId, colId);

		const colInd = findIndex(columns, obj => obj.id == (span ? span.column : colId));
		const rowInd = findIndex(rows, obj => obj.id == (span ? span.row : rowId));

		const x = getTotalWidth(columns.slice(0, colInd));
		const y = getTotalHeight(rows.slice(0, rowInd));

		return {
			x,
			y,
			height: span?.rowspan ? getHeight(rows, span.rowspan, rowInd) : rows[rowInd]?.$height || 0,
			width: span?.colspan ? getWidth(columns, span.colspan, colInd) : columns[colInd]?.$width || 0,
		};
	}
	getSpan(rowId: Id, colId: Id): ISpan {
		if (this.config.spans) {
			let checkRowSpan = null;
			let count = 0;

			const checkItem = (itemId, length) => {
				count++;
				if (itemId === rowId) return true;
				if (count === length) return false;
				const items = this.data.getItems(itemId);
				for (let i = 0; i < items.length; i++) {
					const isTree = items[i].$items;
					if (!isTree) {
						count++;
						if (items[i].id === rowId) return true;
						if (count === length) return false;
					} else {
						return checkRowSpan(items, items[i].id, length);
					}
				}
				return -1;
			};

			checkRowSpan = (items, itemId, length) => {
				const res = checkItem(itemId, length);
				if (res !== -1) {
					count = 0;
					return res;
				}
				items = items || this.data.getItems(this.data.getParent(itemId));
				const nextItem = items[this.data.getIndex(itemId) + 1];
				if (nextItem) {
					return checkRowSpan(items, nextItem.id, length);
				} else {
					const parentId = this.data.getParent(itemId);
					items = this.data.getItems(this.data.getParent(parentId));
					const nextItem = items[this.data.getIndex(parentId) + 1];
					if (nextItem) return checkRowSpan(items, nextItem.id, length);
				}
				return false;
			};

			const spans = this.config.spans;
			const colIndex = this.config.columns.findIndex(col => col.id === colId);
			const index = findIndex(spans, span => {
				const { row, column, rowspan = 1, colspan = 1 } = span;
				const startColIndex = this.config.columns.findIndex(col => col.id === column);
				const endColIndex = startColIndex + colspan;
				return (
					colIndex >= startColIndex &&
					colIndex < endColIndex &&
					!(row !== rowId && rowspan === 1) &&
					checkRowSpan(null, row, rowspan)
				);
			});
			return spans[index];
		}
	}
	protected _adjustColumnsWidth(
		rows: IRow[],
		cols: ICol[],
		adjust: IAdjustBy = this.config.adjust
	): IColumnsWidth {
		let columnsWidth = {};
		if (adjust === "header" || adjust === true) {
			const currentCols = cols.filter(col => col.header);
			const data = getMaxColsWidth(
				this._prepareColumnData(currentCols, "header"),
				currentCols,
				{
					font: "bold 14.4px Arial",
				},
				"header"
			);
			if (data) {
				for (const [key, value] of Object.entries(data)) {
					columnsWidth = Object.assign(columnsWidth, {
						[key]: +value + (isSortable(this.config, this.getColumn(key)) ? 36 : 16),
					});
				}
			}
		}

		if (adjust === "data" || adjust === true) {
			const data = getMaxColsWidth(
				rows,
				cols,
				{
					font: "normal 14.4px Arial",
				},
				"data"
			);
			if (data) {
				for (const [key, value] of Object.entries(data)) {
					const offset = cols[0]?.id === key ? (this.data as any).getMaxLevel() * 24 + 16 : 16;
					if ((columnsWidth[key] && columnsWidth[key] < +value + offset) || !columnsWidth[key]) {
						columnsWidth = Object.assign(columnsWidth, {
							[key]: +value + offset,
						});
					}
				}
			}
		}

		return columnsWidth;
	}
	protected _createCollection(prep: (data: any[]) => any[]) {
		this.data = new TreeGridCollection(
			{
				prep,
				rootId: this.config.rootParent,
			},
			this.events
		);
	}
	protected _getRowIndex(rowId: Id): number {
		return findIndex(this._serialize(), obj => obj.id == rowId);
	}
	protected _setEventHandlers() {
		super._setEventHandlers();

		this.events.on(GridEvents.expand, rowId => {
			const item = this.data.getItem(rowId);
			if (item.$opened) {
				this.collapse(rowId);
			} else {
				this.expand(rowId);
			}
		});
		this.events.detach(GridEvents.filterChange, this);
		this.events.on(GridEvents.filterChange, (val: any, colId: Id, filter: string) => {
			val = val ?? "";
			if (!this._activeFilters) {
				this._activeFilters = {};
			}
			const columnConfig = this.getColumn(colId);
			const conf = columnConfig?.header.filter(
				item => item.content === filter && item.customFilter !== undefined
			)[0];
			if (val !== "") {
				if (
					(columnConfig.editorType === "combobox" ||
						columnConfig.editorType === "select" ||
						columnConfig.editorType === "multiselect") &&
					columnConfig.options
				) {
					if (Array.isArray(val)) {
						val = val.map(item => {
							return (
								columnConfig.options.find(option =>
									typeof option === "string" ? option === item : option.value === item
								)?.id || item
							);
						});
					} else {
						val =
							columnConfig.options.find(option =>
								typeof option === "string" ? option === val : option.value === val
							)?.id ?? val;
					}
				}
				const match = colId => {
					const col = this.getColumn(colId);
					return (val, match, obj) => {
						return this.content[filter].match({ val, match, obj, col });
					};
				};
				this._activeFilters[colId] = {
					by: colId,
					match: val,
					compare: conf?.customFilter ?? match(colId),
				};
			} else {
				delete this._activeFilters[colId];
			}
			this.data.filter();
			this.data.filter(this._activeFilters, { add: true });
		});
	}
	private _groupBy(data: IDataItem[], columnId: string | ((item: IDataItem) => string)) {
		const groups: { [key: string]: IDataItem[] } = {};
		data.reduce((acc, item) => {
			const groupName =
				typeof columnId !== "function" ? item[columnId] && item[columnId].toString() : columnId(item);

			if (!acc[groupName]) {
				acc[groupName] = [];
			}

			acc[groupName].push(item);
			return acc;
		}, groups);

		return Object.entries(groups).map(([key, items]) => {
			items.forEach(i => {
				i.parent = `$group::${key}`;
			});
			return {
				id: `$group::${key}`,
				$groupName: key,
				$customRender: groupRender,
				items,
			};
		});
	}
	private _serialize(driver: DataDriver = DataDriver.json): any[] {
		const data = [];
		this.data.eachChild(this.data.getRoot(), el => {
			if (!el) {
				return;
			}
			const cell = {
				...el,
				$level: el.$level || this.data.getLevel(el.id),
				$items: this.data.haveItems(el.id),
			};

			if (this.data.haveItems(el.id) && el.$opened === undefined) {
				el.$opened = cell.$opened = true;
			}
			data.push(cell);
		});
		const dataDriver = toDataDriver(driver);
		if (dataDriver) {
			return dataDriver.serialize(data);
		}
	}
}
